import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  confirmStaffPasswordReset,
  ApiError,
  StaffResetPasswordError,
} from '../lib/api.js';
import { PasswordInput } from '../components/PasswordInput.js';

// #405 — staff reset-password confirm page. The reset-link email points
// at `https://{slug}.admin.animalloversapp.com/staff/reset-password?token=...`.
// Structurally identical to InviteAccept.tsx (same layout/classes/password
// UX) — the two flows are siblings (magic-link token → new credential),
// just entered from different starting points.

type StaffResetPasswordProps = {
  config: ClientConfig;
};

type UiState =
  | { kind: 'missing_token' }
  | { kind: 'ready' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string; code: string };

const MIN_PASSWORD = 8;

export function StaffResetPassword({ config }: StaffResetPasswordProps): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState<UiState>(() =>
    token.trim() === '' ? { kind: 'missing_token' } : { kind: 'ready' },
  );

  useEffect(() => {
    if (token.trim() === '') {
      setState({ kind: 'missing_token' });
    } else if (state.kind === 'missing_token') {
      setState({ kind: 'ready' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const clientFieldError = useMemo(() => {
    if (password.length > 0 && password.length < MIN_PASSWORD) {
      return `Password must be at least ${MIN_PASSWORD} characters.`;
    }
    if (confirm.length > 0 && confirm !== password) {
      return 'Passwords do not match.';
    }
    return null;
  }, [password, confirm]);

  const formReady =
    token.trim() !== '' &&
    password.length >= MIN_PASSWORD &&
    confirm === password;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formReady || state.kind !== 'ready') return;
    setState({ kind: 'submitting' });
    try {
      await confirmStaffPasswordReset(config.deployment.apiUrl, {
        token,
        newPassword: password,
      });
      setState({ kind: 'success' });
      setPassword('');
      setConfirm('');
    } catch (err) {
      const { message, code } = describeError(err);
      setState({ kind: 'error', message, code });
    }
  };

  if (state.kind === 'missing_token') {
    return (
      <main className="ferro-admin-login">
        <section className="ferro-auth">
          <h1 className="ferro-auth__title">Reset link is missing</h1>
          <p className="ferro-auth__subtitle">
            This page expects a token in the URL. Open the link from your
            password-reset email, or{' '}
            <a href="/staff/forgot-password">request a new one</a>.
          </p>
        </section>
      </main>
    );
  }

  if (state.kind === 'success') {
    return (
      <main className="ferro-admin-login">
        <section className="ferro-auth">
          <h1 className="ferro-auth__title">Password updated</h1>
          <p className="ferro-auth__subtitle">
            Sign in to {config.brand.name} with your new password.
          </p>
          <a href="/login" className="ferro-btn ferro-btn--primary ferro-btn--block">
            Continue to sign in
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="ferro-admin-login">
      <section className="ferro-auth">
        <h1 className="ferro-auth__title">Choose a new password</h1>
        <p className="ferro-auth__subtitle">
          Set a new password for your {config.brand.name} staff account.
        </p>
        <form className="ferro-form" onSubmit={handleSubmit} noValidate>
          <label className="ferro-field" htmlFor="staff-reset-password">
            <span className="ferro-field__label">New password</span>
            <PasswordInput
              id="staff-reset-password"
              name="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={state.kind === 'submitting'}
            />
            <span className="ferro-field__hint">At least {MIN_PASSWORD} characters.</span>
          </label>
          <label className="ferro-field" htmlFor="staff-reset-confirm-password">
            <span className="ferro-field__label">Confirm password</span>
            <PasswordInput
              id="staff-reset-confirm-password"
              name="confirm"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={state.kind === 'submitting'}
            />
          </label>
          {clientFieldError ? (
            <p className="ferro-form__error" role="alert">
              {clientFieldError}
            </p>
          ) : null}
          {state.kind === 'error' ? (
            <p className="ferro-form__error" role="alert">
              {state.message}
              {state.code === 'invalid_reset_token' ? (
                <>
                  {' '}
                  <a href="/staff/forgot-password">Request a new link</a>.
                </>
              ) : null}
            </p>
          ) : null}
          <button
            type="submit"
            className="ferro-btn ferro-btn--primary ferro-btn--block"
            disabled={!formReady || state.kind !== 'ready'}
          >
            {state.kind === 'submitting' ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </section>
    </main>
  );
}

function describeError(err: unknown): { message: string; code: string } {
  if (err instanceof StaffResetPasswordError) {
    return { message: err.message, code: err.code };
  }
  if (err instanceof ApiError) {
    return { message: err.message, code: 'api_error' };
  }
  if (err instanceof Error) {
    return { message: err.message, code: 'unknown' };
  }
  return { message: 'Unexpected error', code: 'unknown' };
}
